import { MapPin } from 'lucide-react';

import { useLanguage } from '@/components/LanguageContext';
import LandingShellV3 from '@/components/landing/v3/LandingShellV3';
import CtaFinalOscuro from '@/components/landing/v3/CtaFinalOscuro';
import FaqAcordeon from '@/components/landing/v3/FaqAcordeon';
import HeroProductoV3 from '@/components/landing/v3/HeroProductoV3';
import CapturaProducto from '@/components/landing/v3/CapturaProducto';
import MapaSeoComparador, { Cuadricula } from '@/components/landing/v3/MapaSeoComparador';
import PorQueImporta from '@/components/landing/v3/PorQueImporta';
import SectionKicker from '@/components/landing/v3/SectionKicker';
import { MIA } from '@/components/landing/v3/mapaSeoDatos';
import { pickLandingV3Content } from '@/pages/landingV3.content';

/**
 * VisibilidadSeoV3 — Mapa SEO y Análisis SEO.
 *
 * Portada desde `Visibilidad SEO.dc.html`. Es la más cargada de las cuatro de
 * Producto: escaneo, comparador de competidores y la tarjeta del Análisis SEO.
 *
 * DOS DECISIONES QUE SE APARTAN DEL DISEÑO:
 *
 * 1. **El comparador es el de la home, no tres capturas.** El original
 *    superpone tres PNG (`pasted-1789741142815-0.png` y sus dos variantes) con
 *    `opacity` para pasar de «Mi negocio» a «Competidor» y a «Diferencia».
 *    Aquí se monta `MapaSeoComparador`, que ya existe y dibuja las tres vistas
 *    de los mismos datos: no hay tres imágenes que mantener sincronizadas y la
 *    diferencia se CALCULA, así que no puede contradecir a las otras dos.
 * 2. **Fuera el chip «Mejor que el 68 % de los locales en tu zona».** No existe
 *    ningún percentil en el Análisis SEO. Ver la cabecera de
 *    `landingV3.producto.js`.
 */
export default function VisibilidadSeoV3() {
  const { language } = useLanguage();
  const raiz = pickLandingV3Content(language);
  const c = raiz.producto.visibilidadSeo;
  const comun = raiz.producto.comun;

  // El comparador se queda con las etiquetas de la home —las tres vistas, la
  // guía, el resumen— y solo cambia lo que es propio de esta página. Son las
  // mismas palabras; duplicarlas aquí sería tener dos sitios donde corregir la
  // misma errata.
  const mapa = { ...raiz.home.mapa, ...c.competidores };

  return (
    <LandingShellV3>
      <HeroProductoV3
        badge={c.hero.badge}
        titulo={c.hero.titulo}
        acento={c.hero.acento}
        sub={c.hero.sub}
        cta={c.hero.cta}
        ctaVer={c.hero.ctaVer}
        anclaVer="#escaneo"
        ctaTrialId="producto.visibilidad.hero.trial"
        ctaVerId="producto.visibilidad.hero.ver"
      >
        <TarjetaMapa titulo={c.hero.consulta} etiqueta={c.hero.etiquetaZona} tono="neutro" />
      </HeroProductoV3>

      <Problema c={c.problema} />
      <Escaneo c={c.escaneo} />
      <MapaSeoComparador c={mapa} />
      <Analisis c={c.analisis} />

      <PorQueImporta
        kicker={c.porQue.kicker}
        cifra={c.porQue.cifra}
        cifraPie={c.porQue.cifraPie}
        titulo={c.porQue.titulo}
        parrafos={[c.porQue.p1, c.porQue.p2, c.porQue.p3]}
      />

      <FaqAcordeon titulo={c.faqTitulo} preguntas={c.faq} />

      <CtaFinalOscuro
        tituloA={c.cierre.titulo}
        acento={c.cierre.acento}
        subtitulo={c.cierre.sub}
        cta={comun.cierre.cta}
        microcopy={comun.cierre.microcopy}
        enterpriseA={comun.cierre.enterpriseA}
        enterpriseCta={comun.cierre.enterpriseCta}
        ctaTrial="producto.visibilidad.cierre.trial"
        ctaVentas="producto.visibilidad.cierre.ventas"
      />
    </LandingShellV3>
  );
}

/** La tarjeta blanca con la consulta arriba y la cuadrícula dentro. */
function TarjetaMapa({ titulo, etiqueta, tono }) {
  return (
    <div className="bg-white border border-v3-line rounded-3xl p-4 v3-shadow max-w-[420px] mx-auto">
      <div className="flex items-center justify-between gap-2.5 px-1 pb-3.5">
        <span className="text-[13.5px] font-bold tracking-[-.01em] text-v3-ink">{titulo}</span>
        <span
          className={`text-[11.5px] font-semibold rounded-full px-2.5 py-1.5 whitespace-nowrap ${
            tono === 'ok' ? 'bg-v3-greensoft text-v3-green' : 'bg-v3-sunken text-v3-body'
          }`}
        >
          {etiqueta}
        </span>
      </div>
      <Cuadricula clave="mio" rejilla={MIA} />
    </div>
  );
}

function Problema({ c }) {
  return (
    <section className="px-6 pt-[60px]">
      <div className="max-w-[1180px] mx-auto bg-v3-surface rounded-3xl p-7 sm:p-10 flex flex-wrap gap-8 lg:gap-11 items-center">
        {/* Los dos pines son la idea entera de la sección en una imagen: el
            mismo negocio, verde en su calle y rojo a 800 m. */}
        <div className="flex-none flex items-end gap-[18px]" aria-hidden="true">
          <div className="text-center">
            <MapPin className="w-10 h-10 text-v3-green" fill="currentColor" strokeWidth={0} />
            <div className="text-[11.5px] font-bold text-v3-green mt-2">{c.pinCerca}</div>
          </div>
          <div className="text-center opacity-90">
            <MapPin className="w-10 h-10 text-v3-red" fill="currentColor" strokeWidth={0} />
            <div className="text-[11.5px] font-bold text-v3-red mt-2">{c.pinLejos}</div>
          </div>
        </div>

        <div className="flex-1 min-w-[280px]">
          <h2 className="text-[26px] md:text-[31px] leading-[1.15] tracking-[-.025em] font-bold text-v3-ink max-w-[24ch]">
            {c.titulo}
          </h2>
          <p className="text-base leading-[1.7] text-v3-body mt-4 max-w-[64ch]">{c.p1}</p>
          <p className="text-base leading-[1.7] text-v3-body mt-3.5 max-w-[64ch]">{c.p2}</p>
        </div>
      </div>
    </section>
  );
}

function Escaneo({ c }) {
  return (
    <section id="escaneo" className="px-6 pt-[76px]">
      <div className="max-w-[1180px] mx-auto">
        <SectionKicker>{c.kicker}</SectionKicker>

        <div className="grid gap-10 lg:grid-cols-2 lg:gap-12 items-center mt-7">
          <div>
            <h2 className="text-[27px] md:text-[32px] leading-[1.15] tracking-[-.025em] font-bold text-v3-ink max-w-[22ch]">
              {c.titulo}
            </h2>
            <p className="text-[16.5px] leading-[1.65] text-v3-body mt-4 max-w-[54ch]">
              {c.cuerpo}
            </p>

            {/* Los tres KPI. En el diseño van en una fila que a 375 px deja
                cada uno en 100 px; aquí se apilan hasta `sm`. */}
            <dl className="flex flex-wrap bg-white border border-v3-line rounded-2xl mt-6 overflow-hidden shadow-[0_1px_3px_rgba(0,0,0,.06)]">
              {c.kpis.map((k) => (
                <div
                  key={k.clave}
                  className="flex-1 basis-full sm:basis-[150px] px-[18px] py-4 border-t sm:border-t-0 sm:border-l border-black/5 first:border-t-0 first:sm:border-l-0"
                >
                  <dt className="text-xs text-v3-muted">{k.l}</dt>
                  <dd className="text-2xl font-semibold tracking-[-.02em] mt-1 text-v3-ink">
                    {k.v}
                  </dd>
                  <dd className="text-xs leading-[1.45] text-v3-faint mt-1.5">{k.d}</dd>
                </div>
              ))}
            </dl>
          </div>

          {/* Captura del panel, no la cuadrícula dibujada. Esta sección
              habla del escáner ENTERO —la búsqueda, el tamaño de zona, los
              KPI de al lado—, y de eso la maqueta solo enseñaba el mapa. El
              hero sigue con la cuadrícula dibujada: ahí lo que se quiere es
              el concepto, y una captura con su barra lateral se lee peor a
              ese tamaño. */}
          <CapturaProducto {...c.captura} />
        </div>
      </div>
    </section>
  );
}

function Analisis({ c }) {
  const t = c.tarjeta;
  const tonos = { mal: 'text-v3-red', medio: 'text-v3-golddeep', bien: 'text-v3-green' };

  return (
    <section className="px-6 pt-[76px]">
      <div className="max-w-[1180px] mx-auto">
        <SectionKicker>{c.kicker}</SectionKicker>

        <div className="grid gap-10 lg:grid-cols-2 lg:gap-12 items-start mt-7">
          <div>
            <h2 className="text-[27px] md:text-[32px] leading-[1.15] tracking-[-.025em] font-bold text-v3-ink max-w-[24ch]">
              {c.titulo}
            </h2>
            {[c.p1, c.p2, c.p3, c.p4].map((p) => (
              <p key={p} className="text-[16.5px] leading-[1.65] text-v3-body mt-4 max-w-[56ch]">
                {p}
              </p>
            ))}
          </div>

          <div className="bg-v3-surface rounded-[20px] p-6 max-w-[470px] mx-auto w-full">
            <div className="bg-white rounded-2xl p-[22px] shadow-[0_1px_3px_rgba(0,0,0,.06)]">
              <div className="flex items-center gap-5 flex-wrap">
                {/* El anillo es decorativo: el número que hay dentro ya lo
                    dice, y un `conic-gradient` no aporta nada a quien no lo ve. */}
                <div
                  aria-hidden="true"
                  className="w-[116px] h-[116px] rounded-full flex items-center justify-center flex-none"
                  style={{ background: 'conic-gradient(#feae2c 0 62%, #ececea 62% 100%)' }}
                >
                  <div className="w-[90px] h-[90px] rounded-full bg-white flex flex-col items-center justify-center">
                    <span className="text-[34px] font-bold tracking-[-.03em] leading-none text-v3-golddeep">
                      {t.puntuacion}
                    </span>
                    <span className="text-[10.5px] text-v3-faint mt-0.5">{t.sobre}</span>
                  </div>
                </div>

                <div className="flex-1 min-w-[150px]">
                  <div className="text-[11.5px] font-bold text-v3-golddeep">{t.nivelEtiqueta}</div>
                  <div className="text-[21px] font-bold tracking-[-.02em] mt-1 text-v3-ink">
                    {t.nivel}
                  </div>
                  <p className="text-[13.5px] leading-[1.5] text-v3-muted mt-1.5">{t.faltan}</p>
                </div>
              </div>

              <dl className="bg-v3-surface rounded-xl mt-[18px] overflow-hidden">
                {t.metricas.map((m) => (
                  <div
                    key={m.clave}
                    className="border-t border-black/5 first:border-t-0 px-3.5 py-2.5 flex items-center gap-3"
                  >
                    <dt className="flex-1 text-[12.5px] text-v3-body min-w-0">{m.l}</dt>
                    <dd className={`text-[13px] font-bold whitespace-nowrap ${tonos[m.tono]}`}>
                      {m.v}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>

            <div className="bg-white rounded-2xl p-[18px] mt-3.5 shadow-[0_1px_3px_rgba(0,0,0,.06)]">
              <div className="text-[11.5px] font-bold text-v3-golddeep">{t.mision.etiqueta}</div>
              <div className="text-base font-bold tracking-[-.01em] leading-[1.35] mt-1 text-v3-ink">
                {t.mision.texto}
              </div>
              <div className="text-[13px] text-v3-green font-semibold mt-1.5">
                {t.mision.puntos}
              </div>
            </div>

            {/* Las seis categorías del `health_score.py`, con sus topes reales. */}
            <ul className="grid grid-cols-[repeat(auto-fit,minmax(130px,1fr))] gap-2 mt-3.5">
              {t.categorias.map((cat) => {
                const pct = cat.p / cat.t;
                const color = pct >= 0.8 ? '#16a34a' : pct >= 0.5 ? '#feae2c' : '#dc2626';
                return (
                  <li
                    key={cat.clave}
                    className="bg-white rounded-xl px-3.5 py-3 shadow-[0_1px_3px_rgba(0,0,0,.06)]"
                  >
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="text-[11.5px] text-v3-body truncate">{cat.n}</span>
                      <span className="text-xs font-bold whitespace-nowrap" style={{ color }}>
                        {cat.p}/{cat.t}
                      </span>
                    </div>
                    <div className="h-[5px] rounded-full bg-v3-track mt-2 overflow-hidden">
                      <div
                        className="h-full rounded-full"
                        style={{ width: `${Math.round(pct * 100)}%`, background: color }}
                      />
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}
